/**
 * Agentic-room composer — the single bottom-of-room widget the user
 * interacts with. Renders inside the shared Slack-style `CommandDeck` (upper deck = attach +
 * mode switcher · middle = prompt · lower = agent streams + scope chips · Send) — the same deck
 * the Network UI mounts.
 *
 * Three composer states (see `composerState.js`):
 *   - IDLE                — start a new run via `pool_execute`.
 *   - RUNNING             — locked for everyone (incl. owner).
 *   - AWAITING_APPROVAL   — Approve / Deny / Modify in the second deck;
 *                            prompt only enables after Modify click.
 *
 * Send semantics:
 *   - IDLE → calls `onSendPrompt(text, { stagingId, attachments })`.
 *   - AWAITING_APPROVAL + modifyMode → calls `onSendModify(text, { ... })`.
 *
 * The composer owns its own draft (text + attachments). The parent owns
 * approval lease state, busy flags, and persisted UI state. This split
 * keeps the room thin and the composer easy to test in isolation.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X as XIcon, Pencil, ClipboardList, ChevronDown, PanelBottom, Globe } from 'lucide-react';
import { applyFormat, formatShortcut, continueList } from '@stepflow/ui-shared/utils/textFormat.js';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { ROOM_UPLOAD_ACCEPT } from '@stepflow/ui-shared/utils/roomUploads.js';
import { parseMentions, mentionAt, mentionSegments, mentionRanges, chipDelete, chipSelection } from '../../utils/contextDiscussion.js';
import { runPrefill, refillPrompt } from '../../utils/projectPipeline.js';
import CommandDeck, { DECK_ICON_BUTTON } from '@stepflow/ui-shared/components/CommandDeck.jsx';
import DeckLeftGroup from '@stepflow/ui-shared/components/DeckLeftGroup.jsx';
import ComposerHint from '@stepflow/ui-shared/components/ComposerHint.jsx';
import ProjectPipeline from './ProjectPipeline.jsx';
import { getRoomContext } from '@stepflow/ui-shared/services/dataPlaneApi';
import WorkbenchPromptAlertBand from '@stepflow/ui-shared/components/WorkbenchPromptAlertBand.jsx';
import BuilderInteractionBand from '../BuilderInteractionBand.jsx';
import { useWorkbenchPromptFieldHeight } from '@/hooks/useWorkbenchPromptFieldHeight.js';
import {
  getWorkbenchPromptInputClassName,
  WORKBENCH_PROMPT_FIELD_STYLE,
} from '@/utils/workbenchPromptFieldClassNames.js';
import { WORKBENCH_OUTLINE_BTN_TOOLBAR } from '@/utils/workbenchOutlineButtonClassNames.js';
import {
  COMPOSER_STATES,
  canSendNow,
  deriveComposerState,
  isPromptEditable,
  placeholderFor,
} from './composerState.js';
import { useChatAttachments } from '../../hooks/useChatAttachments.js';
import AttachmentChips from './AttachmentChips.jsx';
import CodeSnippetDialog from './CodeSnippetDialog.jsx';
import ContextModelPicker from './ContextModelPicker.jsx';
import { prepareRoomContext } from '@/services/dataPlaneApi';

const COMPOSER_TIP_BULLETS = [
  <><kbd className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5">Enter</kbd> sends the message</>,
  <><kbd className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5">Shift+Enter</kbd> inserts a new line</>,
  <>Use the <span className="font-medium">+</span> to attach files</>,
];

const COMPOSER_PROMPT_TIP_KEY = 'stepflow:composer-prompt-tip-dismissed';

/**
 * @typedef {Object} ComposerProps
 * @property {string} networkId
 * @property {string} poolId
 * @property {boolean} canExecute               Caller has permission to start a new run.
 * @property {string|null} uiState              Most recent `UI_STATE.uiState`.
 * @property {{ taskToken: string, taskId: string, agentName?: string }|null} pendingApproval
 * @property {boolean} iHoldClaim
 * @property {boolean} peerHoldsClaim
 * @property {string} [peerLabel]
 * @property {boolean} approvalBusy
 * @property {boolean} sending
 * @property {(text: string, opts: { stagingId: string, attachments: object[], correlationId: string }) => Promise<void>} onSendPrompt
 * @property {() => Promise<void>} onClaim
 * @property {() => Promise<void>} onApprove
 * @property {() => Promise<void>} onDeny
 * @property {(text: string, opts: { stagingId: string, attachments: object[] }) => Promise<void>} onSendModify
 * @property {() => void} onDelegate
 * @property {() => Promise<void>} onEscalate
 */


// (L122) waves are 1-BASED in data (derive_waves) — label them as-is; a legacy 0-based selection shifts by one.
function _waveLabels(waves) {
  const ws = (waves || []).map((w) => Number(w) || 0);
  const shift = ws.includes(0) ? 1 : 0;
  return ws.map((w) => w + shift).join(', ');
}

export default function Composer({
  networkId,
  poolId,
  canExecute,
  uiState,
  pendingApproval,
  iHoldClaim,
  peerHoldsClaim,
  peerLabel,
  approvalBusy = false,
  sending = false,
  /** Lifecycle freeze (M5) — true when the room is PAUSED, RETIRED, or
   *  ARCHIVED. Disables every send / claim / approval action and
   *  surfaces ``frozenReason`` as the textarea placeholder. */
  frozen = false,
  /** Tooltip / placeholder copy for the frozen variant. */
  frozenReason = '',
  /** Pre-flight MCP gate: when set, an enabled authenticated MCP has no
   *  credential for this room — Send is blocked and this copy is shown. */
  mcpGateReason = '',
  onSendPrompt,
  onClaim,
  onApprove,
  onDeny,
  onSendModify,
  onDelegate,
  onEscalate,
  builderCliWait = null,
  onBuilderCliHandled,
  canRespondToBuilderApproval: canRespondApproval = true,
  /** ALWAYS-PRESENT STOP. When a builder run is active this is its runId; the
   *  composer shows a persistent Stop control (both background + interactive)
   *  right where the user is, so a runaway can be killed anytime. */
  activeBuilderRunId = null,
  stoppingRun = false,
  onStopRun,
  /** Verbose (the Shared ``__roomContext__`` toggle, default OFF). When OFF the
   *  under-bar shows ONLY the AI-guidance icon — its popover already carries the
   *  completion basis + suggested-next prompts, so the inline copy is redundant
   *  chrome. When ON the inline completion-basis line + "Next:" quick-prompt
   *  chips are also surfaced. Mirrors the run-block cost telemetry gate. */
  verbose = false,
  /** PROJECT (phased) network — enables the project surfaces: the Plan/Task/Wave mode selector, the
   *  deterministic next-step proposal (``suggestedNextPrompt`` placeholder + Tab), and the inline
   *  "Approve {milestone}" band. False (transactional) → those are hidden. Threaded from the network
   *  summary's ``phases`` (PROJECT-NETWORKS.md §8; the old LLM "suggest next" button is retired). */
  isProject = false,
  /** PROJECT run gating + task binding (Unit F). A project room's run is a deliberate TASK
   *  execution, not a free chat turn. ``planFinalized`` — is the plan finalized (workers may run)?
   *  When false, a normal send is BLOCKED with a banner (finalize in Tasks / Run planner).
   *  ``readyTasks`` — the ready set from ``/tasks`` ([{id,title,leadAgent,milestone}]); drives the
   *  task selector. ``currentMilestone`` — labels the ready set. All ignored for non-project rooms. */
  planFinalized = true,
  /** PROJECT KIND (G0): ``plan_verify`` → people do the work, so Task/Wave are not offered (the server
   *  refuses them too); ``executable`` (default) → Plan | Task | Wave. */
  projectKind = 'executable',
  /** GROOM SCOPE (§2.6): when the user multi-selects task rows in the Tasks tab, the room hands
   *  ``{milestone, taskIds, onClear}`` here. In plan mode, Send becomes a SURGICAL groom confined to
   *  those tasks (the composer shows a chip + passes ``groomScope`` on the run). Null = normal plan. */
  groomScope: groomScopeIn = null,
  /** PLAN TIER (PLAN-INTEGRATION.md §2.5) — the user's per-milestone tier choice from the Tasks tab:
   *  'auto' (default), 'standard', or 'verified'. Sent on a plan run (omitted when 'auto'). */
  planTier = 'auto',
  readyTasks = [],
  currentMilestone = '',
  /** DETERMINISTIC NEXT-PROMPT PROPOSAL — a low-key suggestion computed from the plan roadmap (e.g.
   *  "Plan Architecture wave 1", "Approve Scoping"). Shown as the prompt PLACEHOLDER when the field
   *  is empty; pressing Tab on an empty field fills it in as real editable text. '' → normal
   *  placeholder. No LLM — instant. */
  suggestedNextPrompt = '',
  /** EXPERT ONBOARDING (EXPERT-NETWORKS.md §7) — ``{text, nonce}``: a request pre-filled for the person to review +
   *  send (never auto-sent); fills the box only when it's empty. */
  seedPrompt = null,
  /** GROOM SCOPE the suggestion is confined to ({milestone, taskIds, waves?}) — applied on Send ONLY
   *  when the user accepted the suggestion verbatim (Tab) and hasn't picked an explicit Tasks-tab
   *  groom. Keeps a "Plan X wave N" suggestion's run actually scoped to wave N. Null → plain plan. */
  suggestedGroomScope = null,
  /** INLINE MILESTONE APPROVE — when the current milestone's plan is complete (all tasks detailed,
   *  not yet approved), the room passes ``{id, name}`` so the status band shows an "Approve {name}"
   *  button. Null → no button. ``onApproveMilestone(id)`` fires the approve; ``approveMilestoneBusy``
   *  disables it while in flight. */
  approvableMilestone = null,
  // THE SUGGESTED PLAN UPDATE (owner 2026-10-21): {fromVersion, toVersion, milestones, taskCount} when this room used
  // an older suggestion and changed nothing since — offered on the banner (Update / Dismiss); null otherwise.
  suggestedUpdate = null,
  onSuggestedUpdate = null,   // (action: 'update' | 'dismiss') => Promise
  onApproveMilestone = () => {},
  approveMilestoneBusy = false,
  /** REVIEW JUMP — the milestone the user would open to read/edit ({id, name}); the status band
   *  shows a low-key "Review" link that jumps to it in the Tasks tab. ``onReviewMilestone(id)``
   *  performs the jump. Null → no link. */
  reviewMilestone = null,
  onReviewMilestone = () => {},
  /** GUIDED JOURNEY (PROJECT-NETWORKS §8) — the active pipeline stage ('plan'|'review'|'finalize'|
   *  'run') for the cursor milestone, derived upstream from the ledger. Renders the Plan→Review→
   *  Finalize→Run stepper so the user always sees where they are. '' → no stepper (non-project). */
  projectStage = '',
  /** CONTEXT MODE (MODES.md §3, M4) — the room discussion. ``discussionAgents`` = [{id,name}] for the picker
   *  (default "No agent" = people only); ``onSendContext(text, {agentId})`` posts (nothing runs the network);
   *  ``onModeChange(mode)`` tells the room which mode is active (it shows the discussion in Context). */
  discussionAgents = [],
  /** PEOPLE you can @-tag (MENTIONS.md): this room's members [{sub, name, email}] (not you) — tagging NOTIFIES them */
  mentionPeople = [],
  // EXPERT ROOMS (EXPERT-ROOMS.md §8, X4): the expert TEAMS serving this room ([{label, expertNetworkId}]) — "@Platform
  // we need an account" opens that Team's request form (the message becomes its note) instead of sending a message
  mentionTeams = [],
  onTeamRequest = null,
  // CONSULT (EXPERT-ROOMS.md §13): the experts this room can ask by ONE name (``@AWS CCoE``) — a message naming one
  // goes to that expert as a question (its Lead routes it; the answer comes back in this message's thread)
  mentionExperts = [],
  onExpertConsult = null,
  // in an expert's room: {count, first, onReview} — questions waiting on this team (the banner above the prompt)
  consultsWaiting = null,
  /** ROOM PRESENCE (MENTIONS.md §6): ``onTyping()`` on each keystroke with text, ``onTypingStop()`` on send / clear /
   *  blur (the room throttles + publishes); ``typingLine`` = "Pani is typing…" shown above the field ('' = hidden). */
  /** THE ROOM'S MODEL for Context (MODES.md §3e): '' | fast | think | reason; owner-only change (``canSetContextModel``);
   *  ``onSetContextModel(tier)`` → Promise (rejects with the server's reason). */
  contextModel = '',
  canSetContextModel = false,
  contextModelSetByName = '',
  onSetContextModel = null,
  /** STAND-IN ADVICE (STAND-IN.md §7) on the Run gate (``gateAdvice``) and the Plan "Approve milestone" band
   *  (``planAdvice``) — nodes from the room. */
  gateAdvice = null,
  planAdvice = null,
  /** UPPER-DECK TOOLS after the format buttons (the room's own — e.g. STAND-IN.md's "Your stand-in"). */
  deckTools = null,
  /** UPPER-DECK STATUS beside 📎 (e.g. the Context "N new items · Build context" chip — right-aligned). */
  deckStatus = null,
  /** THE ROOM'S INTERNET SEARCH (MODES.md §10.9) — on: the agent you Ask / Do may search the web (default) */
  internetSearch = true,
  onTyping = null,
  onTypingStop = null,
  typingLine = '',
  onSendContext = null,
  onModeChange = () => {},
  /** M5: leaving Context for Plan asks the room first (``async (to) => proceed?``) — it offers to build the
   *  context from the discussion. Absent → switch straight away. */
  onLeaveContext = null,
  /** Does the project have any planned task yet? A room with no plan starts in Context (the journey's first
   *  step), one with a plan but nothing runnable in Plan, a runnable one in Run task. */
  hasPlan = true,
  /** THE ROOM'S MODE (MODES.md §7) — when the room owns the mode (``roomMode`` given: context|plan|run|verify) the
   *  composer SHOWS it and a mode click REQUESTS a switch for everyone (``onRequestMode(mode) → Promise<bool>``);
   *  ``canSwitchMode`` false → the buttons explain who can. The Run ▾ style and the Context agent/Ask-Do stay the
   *  person's own. Absent → the composer keeps its own mode (legacy/tests). */
  roomMode = undefined,
  canSwitchMode = true,
  onRequestMode = null,
  /** ROOM TYPE (MODES.md §8): a Context-only room offers ONLY Context (no Plan / Run / Verify). */
  contextOnly = false,
  // PRODUCT.md §2 — a room before Plan talks (Ask/Do/Context); its network still runs as a whole on purpose:
  // ``runNetwork`` = offer "Run network" (the old prompt-runs-the-network send) beside Send. ``keptConversational``
  // = the owner holds the room at Converse → no Do.
  runNetwork = false,
  keptConversational = false,
  // PRODUCT.md §3 — the Plan gate: a room before Plan offers "Plan…" (draft milestones from its context)
  onOpenPlanGate = null,
  /** The room's mode / type is still loading → the switcher shows a placeholder (never a guessed mode that then
   *  jumps) and Send waits — what Send does depends on the mode. */
  modeLoading = false,
  /** AGENT STREAMS (the dock) — the lower deck's toggle; omitted → no toggle (callers without a dock). */
  dockOpen = false,
  onToggleDock = null,
}) {
  // RUN TASKS (MODES.md §2, M3): a Tasks-tab selection in a LOCKED milestone arrives as ``kind:'run'`` — it
  // is a run selection (Run tasks), never a groom scope.
  const runSelection = groomScopeIn && groomScopeIn.kind === 'run' && (groomScopeIn.taskIds || []).length
    ? groomScopeIn : null;
  const groomScope = runSelection ? null : groomScopeIn;
  const composerState = deriveComposerState({ uiState, pendingApproval });
  // While the room is RUNNING, guidance (working premise + next-suggestions +
  // completion) is stale/irrelevant — hide it; it returns when the room is IDLE.
  const roomIsRunning = composerState === COMPOSER_STATES.RUNNING;

  const [prompt, setPrompt] = useState('');
  useEffect(() => {
    const t = seedPrompt && String(seedPrompt.text || '').trim();
    if (t) setPrompt((p) => (String(p || '').trim() ? p : t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedPrompt && seedPrompt.nonce]);
  // Tracks whether the CURRENT prompt is a verbatim-accepted suggestion (via Tab). Cleared the moment
  // the user edits it. Used to decide whether to apply ``suggestedGroomScope`` on Send — so an
  // accepted "Plan X wave N" is truly wave-scoped, but a hand-typed prompt is not silently confined.
  const [suggestionAccepted, setSuggestionAccepted] = useState(false);
  const [modifyMode, setModifyMode] = useState(false);
  // PROJECT MODE (Unit F + Plan-mode consolidation) — project rooms only. THREE modes in one
  // selector, so the composer's mode IS "what Send does":
  //   'plan' — re-plan/groom: prompt + uploaded artifacts → planMode run → drafts into __planner__
  //            for review in the Tasks tab. The skeleton is only a starting point; the PROMPT is
  //            the substance, so Send requires a prompt (or an attachment).
  //   'task' — execute ONE task (Auto next-ready, or a specific ready task id).
  //   'wave' — execute the whole ready set in parallel.
  // Non-project rooms ignore this entirely (a plain chat/exec run).
  const [projectMode, setProjectMode] = useState('task');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  // The ready set as passed; keep only well-formed rows. AUTO preview = the first ready task.
  const readyList = useMemo(
    () => (Array.isArray(readyTasks) ? readyTasks.filter((t) => t && t.id) : []),
    [readyTasks],
  );
  const autoTask = readyList[0] || null;
  // Task/Wave are RUNNABLE only when the plan is finalized AND something is ready; Plan is always
  // available (you plan BEFORE finalizing). Default the mode sensibly: PLAN when there's nothing to
  // run yet (unplanned/empty ledger — the user must plan first), else TASK (deliberate execution).
  const verifyOnly = projectKind === 'plan_verify';
  const canRunTasks = isProject && !verifyOnly && planFinalized && readyList.length > 0;
  // VERIFY (G0b): agents CHECK uploaded proof against a task's definition of done (both kinds).
  const canVerify = isProject && planFinalized && readyList.length > 0;
  // JOURNEY DEFAULT (MODES.md §1): until the user picks a mode, the composer follows the project — no plan yet →
  // Context (a NEW room starts by talking it through), a plan but nothing runnable → Plan, something ready →
  // Run task. Once the user picks, it's theirs; we only move off a mode that stopped being available.
  const userPickedModeRef = useRef(false);
  const roomControlled = roomMode !== undefined && typeof onRequestMode === 'function';
  const _RUN_STYLES = ['task', 'wave', 'tasks'];
  const pickMode = async (m) => {
    if (!roomControlled) { userPickedModeRef.current = true; setProjectMode(m); return; }
    // the ROOM's mode: a Run style is the person's own; the mode itself is switched for everyone
    const target = _RUN_STYLES.includes(m) ? 'run' : m;
    if (_RUN_STYLES.includes(m)) setRunStyle(m);
    if (target === roomMode) return;
    if (!canSwitchMode) return;
    await onRequestMode(target);
  };
  const _contextAvail = typeof onSendContext === 'function';
  // a person who can't switch the ROOM's mode sees it but can't move it (the server enforces the same)
  const _locked = (m) => roomControlled && !canSwitchMode && roomMode !== m;
  const _LOCK_TITLE = "Only the room's owners and builders switch its mode";
  useEffect(() => {
    if (!isProject || roomControlled) return;
    const modeOk = projectMode === 'plan' || (projectMode === 'context' && _contextAvail)
      || (projectMode === 'verify' ? canVerify : canRunTasks);
    const journey = canRunTasks ? 'task' : (!hasPlan && _contextAvail ? 'context' : 'plan');
    if (!userPickedModeRef.current) {
      if (projectMode !== journey && !(projectMode === 'tasks' && canRunTasks)) setProjectMode(journey);
      return;
    }
    if (!modeOk) setProjectMode(journey === 'task' ? 'plan' : journey);
  }, [isProject, canRunTasks, canVerify, projectMode, hasPlan, _contextAvail, roomControlled]);
  // If a previously-selected task fell out of the ready set (finished/blocked/wave advanced),
  // snap back to AUTO so we never send a stale id.
  useEffect(() => {
    if (selectedTaskId && !readyList.some((t) => t.id === selectedTaskId)) setSelectedTaskId('');
  }, [selectedTaskId, readyList]);
  // Project RUN GATE (Task/Wave only): a task/wave run is blocked until the plan is finalized. PLAN
  // mode is exempt (that's how you get TO finalized). Non-project rooms are never gated.
  const projectRunBlocked = isProject && projectMode !== 'plan' && projectMode !== 'context' && !planFinalized;
  // CONTEXT MODE: who answers (''= No agent — people only); the room learns the active mode.
  const contextMode = isProject && projectMode === 'context' && typeof onSendContext === 'function';
  const [contextAgent, setContextAgent] = useState('');
  // DIRECT WORK (MODES.md §6): Ask (default — it answers, changes nothing) | Do (it works in its own area)
  const [contextDo, setContextDo] = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);   // the `</>` code snippet dialog (CONTEXT-DOCS §P4)
  useEffect(() => { onModeChange(projectMode); }, [projectMode, onModeChange]);
  useEffect(() => {
    if (contextAgent && !(discussionAgents || []).some((a) => a.id === contextAgent)) setContextAgent('');
  }, [contextAgent, discussionAgents]);
  // @MENTIONS (MODES.md §10.6): "@Architect @Security thoughts?" asks each named agent — they override the picker.
  // ``mentionQ`` = the partial "@que" the caret is in → the picker under the prompt.
  const mentioned = useMemo(() => (contextMode ? parseMentions(prompt, discussionAgents) : []),
    [contextMode, prompt, discussionAgents]);
  const [mentionQ, setMentionQ] = useState(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  // PEOPLE tagged in this draft (MENTIONS.md) — kept by id, not by text, so a rename / a same-named person can't confuse
  // who gets notified; a tag whose "@Name" was deleted from the text is dropped at send
  const [taggedPeople, setTaggedPeople] = useState([]);
  const [taggedTeams, setTaggedTeams] = useState([]);
  const [taggedExperts, setTaggedExperts] = useState([]);
  const canMention = contextMode || (mentionPeople || []).length > 0 || (mentionTeams || []).length > 0
    || (mentionExperts || []).length > 0;
  const mentionMatches = useMemo(() => {
    if (!mentionQ) return [];
    const q = mentionQ.query.toLowerCase();
    // ALREADY TAGGED → not offered again (a tag is once per message; delete the chip to free it)
    // (the "@que" being typed right now isn't a tag yet — leave it out, or a fully typed name would hide itself)
    const rest = prompt.slice(0, mentionQ.start) + prompt.slice(mentionQ.start + 1 + mentionQ.query.length);
    const taken = new Set(taggedPeople.filter((t) => rest.includes(`@${t.label}`)).map((t) => t.sub));
    const takenAgents = new Set(contextMode ? parseMentions(rest, discussionAgents) : []);
    const people = (mentionPeople || [])
      .filter((p) => p && p.sub && !taken.has(p.sub))
      .filter((p) => (!q || String(p.name || '').toLowerCase().includes(q) || String(p.email || '').toLowerCase().includes(q)))
      .map((p) => ({ kind: 'person', key: `p:${p.sub}`, sub: p.sub, label: p.name || p.email, email: p.email }));
    const agents = contextMode ? (discussionAgents || [])
      .filter((a) => a && !takenAgents.has(a.id))
      .filter((a) => !q || String(a.name || '').toLowerCase().includes(q) || String(a.id || '').toLowerCase().includes(q))
      .map((a) => ({ kind: 'agent', key: `a:${a.id}`, id: a.id, label: a.name || a.id })) : [];
    const teams = (mentionTeams || [])
      .filter((t) => t && t.label && !rest.includes(`@${t.label}`))
      .filter((t) => !q || String(t.label).toLowerCase().includes(q))
      .map((t) => ({ kind: 'team', key: `t:${t.expertNetworkId}:${t.label}`, label: t.label, expertNetworkId: t.expertNetworkId }));
    const experts = (mentionExperts || [])
      .filter((x) => x && x.name && !rest.includes(`@${x.name}`))
      .filter((x) => !q || String(x.name).toLowerCase().includes(q))
      .map((x) => ({ kind: 'expert', key: `x:${x.expertNetworkId}`, label: x.name, expertNetworkId: x.expertNetworkId }));
    // EVERY match — people first, then agents, then experts, then expert teams; the list scrolls
    return [...people, ...agents, ...experts, ...teams];
  }, [mentionQ, discussionAgents, mentionPeople, mentionTeams, mentionExperts, contextMode, prompt, taggedPeople]);
  const pickMention = (m) => {
    if (!mentionQ || !m) return;
    const el = promptFieldRef.current;
    const caret = el ? el.selectionStart : prompt.length;
    const label = String(m.label || m.id || '');
    const next = `${prompt.slice(0, mentionQ.start)}@${label} ${prompt.slice(caret)}`;
    setPrompt(next);
    if (m.kind === 'person') {
      setTaggedPeople((cur) => (cur.some((t) => t.sub === m.sub) ? cur : [...cur, { sub: m.sub, label }]));
    }
    if (m.kind === 'team') {
      setTaggedTeams((cur) => (cur.some((t) => t.label === label) ? cur : [...cur, { label, expertNetworkId: m.expertNetworkId }]));
    }
    if (m.kind === 'expert') {
      setTaggedExperts((cur) => (cur.some((t) => t.label === label) ? cur : [...cur, { label, expertNetworkId: m.expertNetworkId }]));
    }
    setMentionQ(null);
    setTimeout(() => { const f = promptFieldRef.current; if (f) { const at = mentionQ.start + label.length + 2; f.focus(); f.setSelectionRange(at, at); } }, 0);
  };
  // the people still tagged in ``text`` (their "@Name" survived edits) → their subs
  // the picked @names drawn as TAGS (MENTIONS.md §1): a mirror layer behind the field paints the text, the field's own
  // text goes transparent (caret + selection stay) — a textarea can't style part of its text
  const mentionTags = useMemo(() => {
    const labels = [
      ...(contextMode ? (discussionAgents || []).flatMap((a) => [a.name, a.id].filter(Boolean).map((label) => ({ label, kind: 'agent' }))) : []),
      ...taggedPeople.map((t) => ({ label: t.label, kind: 'person' })),
      ...taggedTeams.map((t) => ({ label: t.label, kind: 'agent' })),
      ...taggedExperts.map((t) => ({ label: t.label, kind: 'agent' })),
    ];
    const segs = mentionSegments(prompt, labels);
    return segs.some((x) => x.tag) ? segs : null;
  }, [prompt, discussionAgents, taggedPeople, taggedTeams, contextMode]);
  const mirrorRef = useRef(null);
  // CHIPS (MENTIONS.md §1): a tag is ONE unit — where each sits, and which one is selected (clicked)
  const chipRanges = useMemo(() => mentionRanges(mentionTags || []), [mentionTags]);
  const [chipSel, setChipSel] = useState(null);
  const syncChipSel = (el) => {
    if (!el) return;
    const r = chipRanges.find((x) => x.start === el.selectionStart && x.end === el.selectionEnd);
    setChipSel(r ? r.start : null);
  };
  const mentionSubsFor = (text) => taggedPeople.filter((t) => String(text || '').includes(`@${t.label}`)).map((t) => t.sub);
  // Do works with ONE agent at a time (it changes things); several mentions are an Ask
  const doTooMany = contextMode && contextDo && mentioned.length > 1;

  // CONNECTOR READINESS WARNING (TOOL-READINESS §D — SOFT WARN, not a block). The task(s) about to
  // run may need connectors that aren't tested yet; surface it so the user can credential first,
  // but never block (a missing cred may be intentional/optional; the builder fails safe anyway).
  const connectorWarning = useMemo(() => {
    // Plan mode writes nothing → no connectors needed; only warn for task/wave EXECUTION runs.
    if (!isProject || !planFinalized || projectMode === 'plan') return null;
    // Which task(s) will this send touch? Task mode → the selected (or auto next-ready) task;
    // Wave mode → the whole ready set.
    const scope = projectMode === 'wave'
      ? readyList
      : projectMode === 'tasks'
      ? readyList.filter((t) => ((runSelection && runSelection.taskIds) || []).includes(t.id))
      : [readyList.find((t) => t.id === selectedTaskId) || autoTask].filter(Boolean);
    const unready = [];
    for (const t of scope) {
      for (const rc of (t.requiredConnectors || [])) {
        if (String(rc.credentialStatus || '') !== 'tested_ok') {
          unready.push(`${rc.agent}·${rc.connectorClass}`);
        }
      }
    }
    return unready.length ? Array.from(new Set(unready)) : null;
  }, [isProject, planFinalized, projectMode, readyList, selectedTaskId, autoTask]);

  // Live room guidance (advisory): suggested next prompts + completion, from the
  // room-context synthesis (CONTEXT.md §15d). Surfaced in the "i" popover; null
  // when Room Context is off or no synthesis has run yet. Best-effort — a failed
  // fetch just leaves the static tips. Refetches when the run finishes (uiState
  // settles) so suggestions reflect the latest state.
  const [roomGuidance, setRoomGuidance] = useState(null);
  const [roomGuidanceAt, setRoomGuidanceAt] = useState('');
  // The room's WORKING PREMISE (established canon/spine) from the same synthesis.
  // Shown in the composer band so the user sees what the room is anchored to —
  // the same text the "Room guidance" popover renders under "Working premise".
  const [roomPremise, setRoomPremise] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!networkId || !poolId) return undefined;
    getRoomContext(networkId, { pool: poolId })
      .then((res) => {
        if (cancelled) return;
        const g = res?.summary?.guidance || null;
        setRoomGuidance(g && (g.suggestedPrompts?.length || g.completion) ? g : null);
        setRoomPremise(String(g?.workingPremise || '').trim());
        setRoomGuidanceAt(res?.summary?.extractedAt || '');
      })
      .catch(() => { if (!cancelled) { setRoomGuidance(null); setRoomPremise(''); } });
    return () => { cancelled = true; };
    // Refetch on room switch + when a run settles (uiState leaves RUNNING).
  }, [networkId, poolId, uiState]);
  const [composerTipDismissed, setComposerTipDismissed] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(COMPOSER_PROMPT_TIP_KEY) === '1';
    } catch {
      return false;
    }
  });
  const promptFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  // TEXT FORMAT (MODES.md §3c) — Bold / Italic / Normal / bullets / numbers as MARKDOWN in the plain field (no rich-
  // text library); the selection is kept so a second click toggles it back.
  const setPromptSel = (next) => {
    setPrompt(next.value);
    setSuggestionAccepted(false);
    requestAnimationFrame(() => {
      const el = promptFieldRef.current;
      if (el) { el.focus(); el.setSelectionRange(next.start, next.end); }
    });
  };
  const formatPrompt = (action) => {
    const el = promptFieldRef.current;
    const s = el ? el.selectionStart : prompt.length;
    const e = el ? el.selectionEnd : prompt.length;
    setPromptSel(applyFormat(action, prompt, s, e));
  };
  // SUGGEST-NEXT reconciled away: the LLM "Suggest next" button (project driver §8 v1) is RETIRED in
  // favour of the DETERMINISTIC roadmap proposal (``suggestedNextPrompt`` placeholder + Tab-to-fill,
  // and the "Approve {milestone}" band). That path is instant, correct in the draft/approve/wave
  // states where the LLM draft returned "No next step to suggest yet.", and reads the ledger
  // dimensions the LLM draft ignored (approvals + planning cursor + waves). One next-step pane; no
  // parallel LLM suggester. See MILESTONE-SCOPED-PLANNING §next-step + PROJECT-NETWORKS §8.
  // (Run-planner button removed — planning is now the composer's "Plan" MODE: pick Plan, write the
  // guidance / attach artifacts, and Send fires a planMode run. See fireSend + the mode selector.)
  const promptHeight = useWorkbenchPromptFieldHeight(promptFieldRef, prompt, { maxHeightPx: 200 });
  // Past ~3 lines flip the pill → two-deck card (text top, buttons below).
  // Gate on the value (empty ⇒ single-row) so it snaps back the instant the
  // prompt is sent/cleared, not after the next height re-measure.
  const composerMultiline = !!String(prompt || '').trim() && promptHeight > 64;

  // §26.4 — when the "+" uploads finish, kick the room-uploads consolidation immediately (overlap
  // the user typing). The worker publishes CONTEXT_READY (by stagingId); the room gates Send on it.
  // Fire-and-forget; the send has a safety timeout so a failed kick never blocks.
  // CONTEXT MODE (CONTEXT-DOCS.md §P2/§P3): a document shared in the discussion is read → digested → folded into the
  // PROJECT context by the build; the run-time room-uploads consolidation would ingest it a SECOND time (two copies
  // reaching agents, and PDFs/Office set aside there anyway). Only Run/Plan uploads take that path.
  const contextModeRef = useRef(contextMode);
  contextModeRef.current = contextMode;
  const attachments = useChatAttachments(networkId, {
    onUploaded: (descriptors, stagingId) => {
      if (contextModeRef.current) return;
      const atts = (descriptors || []).map((d) => ({
        bucket: d.bucket, s3Key: d.s3Key, filename: d.filename || d.fileName,
      }));
      if (!atts.length) return;
      prepareRoomContext(networkId, { pool: poolId, stagingId, attachments: atts })
        .catch(() => { /* best-effort — the runner also kicks as a backstop */ });
    },
  });

  // Leaving AWAITING_APPROVAL clears any half-typed modify draft so the
  // next IDLE turn doesn't inherit stale text.
  useEffect(() => {
    if (composerState !== COMPOSER_STATES.AWAITING_APPROVAL && modifyMode) {
      setModifyMode(false);
    }
  }, [composerState, modifyMode]);

  const editable = isPromptEditable({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    busy: sending || approvalBusy,
    frozen,
  });
  // RUN ▾ (MODES.md §2): "Run" is a prefix — Run task (default) · Run wave. The last-used style is what the
  // Run button selects; the arrow switches it. Choosing one PREFILLS the prompt with the task ids (people +
  // agents read them; the binding below is what decides what runs), keeping the user's own notes.
  const [runStyle, setRunStyle] = useState('task');
  // the room's mode → what this composer shows (Run = the person's last Run style)
  useEffect(() => {
    if (!isProject || !roomControlled || !roomMode) return;
    setProjectMode(roomMode === 'run' ? runStyle : roomMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, roomControlled, roomMode, runStyle]);
  const _runSelKey = runSelection ? (runSelection.layers || []).map((l) => l.join('+')).join('>') : '';
  const _runSteps = runSelection ? (runSelection.layers || []).length : 0;
  const _runCount = runSelection ? runSelection.taskIds.length : 0;
  const [runMenuOpen, setRunMenuOpen] = useState(false);
  const runMenuRef = useRef(null);
  const lastPrefillRef = useRef('');
  useEffect(() => {
    if (!isProject || !editable) return;
    if (projectMode === 'task' || projectMode === 'wave' || projectMode === 'tasks') {
      const pf = runPrefill(projectMode, { readyList, selectedTaskId, milestone: currentMilestone, runSelection });
      const old = lastPrefillRef.current;                    // captured NOW (the updater runs later)
      setPrompt((p) => refillPrompt(p, old, pf));
      lastPrefillRef.current = pf;
    } else if (lastPrefillRef.current) {
      const old = lastPrefillRef.current;
      setPrompt((p) => (p === old ? '' : p));                // leaving Run: drop an untouched prefill
      lastPrefillRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, editable, projectMode, selectedTaskId, readyList, currentMilestone, _runSelKey]);
  // A run selection switches the composer to Run tasks; clearing it falls back to Run task.
  useEffect(() => {
    if (!isProject) return;
    if (_runSelKey) { setRunStyle('tasks'); if (!roomControlled) setProjectMode('tasks'); }
    else { setRunStyle((s) => (s === 'tasks' ? 'task' : s)); if (!roomControlled) setProjectMode((m) => (m === 'tasks' ? 'task' : m)); }
  }, [isProject, _runSelKey]);
  // GROOM → PROMPT PREFILL (WYSIWYG): instead of a floating chip, a Tasks-tab selection PREFILLS the
  // prompt with an editable action line — the scope is visible as text the user can edit/extend.
  // Wave-select = bounded PLANNING; row-select = REFINE. The structured ``groomScope`` still rides
  // the run (scope enforcement is intact — the text alone would plan the whole milestone), surfaced
  // by a tiny inline hint under the input (not a chip). Prefills only when the box is empty, so we
  // never clobber something the user is typing.
  const _groomKey = groomScope && groomScope.milestone
    ? `${groomScope.milestone}|${groomScope.epic || ''}|${groomScope.epicParent || ''}|${(groomScope.waves || []).join(',')}|${(groomScope.taskIds || []).join(',')}`
    : '';
  const _lastGroomKeyRef = useRef('');
  // The EXACT text we last auto-prefilled. When the selection changes we may overwrite the box ONLY
  // if it still holds this string (i.e. our own prefill, untouched) — so refining the selection
  // (add wave 5) updates the prompt to match the hint, but we never clobber text the user typed.
  const _lastPrefillRef = useRef('');
  useEffect(() => {
    if (_groomKey === _lastGroomKeyRef.current) return;   // same selection → don't re-prefill
    _lastGroomKeyRef.current = _groomKey;
    if (!_groomKey) {
      // Selection CLEARED → drop our prefill if it's still there untouched (leave a typed draft).
      setPrompt((cur) => (String(cur || '') === _lastPrefillRef.current ? '' : cur));
      _lastPrefillRef.current = '';
      return;
    }
    if (!editable) return;
    const mName = groomScope.milestoneName || groomScope.milestone;
    let line;
    if (groomScope.kind === 'epic') {
      // EPIC PARTITION (§0.1): "select an epic → create/refine its ≤10 tasks" — the intuitive
      // sibling of wave-select. Prefills the create prompt with the epic's story so Send authors
      // exactly this epic's leaf tasks (the runner expands the epic → its task ids).
      const story = groomScope.epicStory || groomScope.epicParent || groomScope.epic;
      line = `Create tasks for ${mName} → ${story}`;
    } else if (groomScope.kind === 'wave') {
      line = `Plan ${mName} wave${(groomScope.waves || []).length === 1 ? '' : 's'} `
        + `${_waveLabels(groomScope.waves)}`;
    } else {
      line = `Refine ${mName} (${(groomScope.taskIds || []).length} task`
        + `${(groomScope.taskIds || []).length === 1 ? '' : 's'})`;
    }
    setPrompt((cur) => {
      const c = String(cur || '');
      // Overwrite when the box is empty OR still holds our prior prefill verbatim (selection refined);
      // otherwise the user has typed → keep their text (only the scope hint updates).
      // …the Run ▾ prefill is machine text too (an untouched "Run M0-001 — …" gives way to a groom line)
      const mine = !c.trim() || c === _lastPrefillRef.current || (!!lastPrefillRef.current && c === lastPrefillRef.current);
      if (mine) { _lastPrefillRef.current = line; lastPrefillRef.current = ''; return line; }
      return cur;   // user-edited → don't touch; also don't claim ``line`` as ours
    });
    setTimeout(() => promptFieldRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_groomKey]);
  // DETERMINISTIC SUGGESTION as placeholder (Tab-to-fill): show ONLY when the field is empty +
  // editable + the room is idle (not mid-run) + we actually have a proposal. So it never fights a
  // half-typed draft, and Tab keeps its normal tab-out behavior the moment you start typing.
  const showSuggestGhost = Boolean(
    suggestedNextPrompt && editable && !roomIsRunning && !contextMode && !String(prompt || '').trim(),
  );
  // Attachments in Modify-mode require `pool_approval_handler` to forward
  // `attachmentStagingId` / `attachmentInput` into the resumed Step
  // Function — that backend extension lands separately. Until then the
  // composer keeps the Attach button visible but disabled during Modify
  // so the room still feels uniform without misleading the user about
  // what reaches the agent.
  const attachAllowed = composerState !== COMPOSER_STATES.AWAITING_APPROVAL || !modifyMode;
  const attachEnabled = editable && attachAllowed && !frozen;
  const _baseSendReady = canSendNow({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    promptText: prompt,
    hasUploading: attachments.hasUploading,
    busy: sending || approvalBusy,
    frozen,
    mcpGateReason,
  });
  // PLAN mode substance rule: the playbook skeleton is only a starting point — a plan run needs
  // SUBSTANCE, so Send requires a PROMPT or an uploaded ARTIFACT (canSendNow already requires
  // prompt text; we additionally allow an attachment-only plan send). For Task/Wave the base gate
  // (prompt required) stands. Empty-and-no-attachment plan → Send stays disabled.
  const _hasAttachment = Array.isArray(attachments.descriptors) && attachments.descriptors.length > 0;
  // Plan + Context: a file alone is enough substance (Context: "shared sow.pdf")
  const sendReady = !modeLoading && !doTooMany && ((isProject && (projectMode === 'plan' || contextMode))
    ? (_baseSendReady || (!frozen && !sending && !approvalBusy && !attachments.hasUploading
                          && Boolean(canExecute) && _hasAttachment
                          && composerState === COMPOSER_STATES.IDLE))
    : _baseSendReady);
  const _basePlaceholder = placeholderFor({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    peerLabel,
    frozen,
    frozenReason,
    mcpGateReason,
  });
  // WHO ANSWERS (MODES.md §10.6) — ONE answer for the picker AND @mentions: the agents named with @ (they win, as on
  // send), else the picked agent. Everything agent-specific (Ask/Do, "Uses", the Send label, the hint) follows it, so
  // tagging @Architect lights up exactly what picking Architect does.
  const askAgentIds = mentioned.length ? mentioned : (contextAgent ? [contextAgent] : []);
  const _askOne = askAgentIds.length === 1 ? askAgentIds[0] : '';
  const _ctxAgent = (discussionAgents || []).find((a) => a.id === _askOne) || {};
  const _ctxName = _ctxAgent.name || _askOne;
  // only a BUILDER can do work (CONTEXT-DOCS §H1); an agent list without the flag (older server) keeps Do available.
  // Do works with ONE agent — several named → Ask only
  const _ctxIsBuilder = !!_askOne && _ctxAgent.builder !== false && !keptConversational;
  // WHAT SEND DOES — named on the button, so nothing is hidden: Send · Ask X · X: do it · Ask N agents
  // a worker (or a second agent) after Do was on → back to Ask (Do never silently falls back to an answer)
  useEffect(() => { if (contextDo && !_ctxIsBuilder) setContextDo(false); }, [contextDo, _ctxIsBuilder]);
  const sendLabel = contextMode && editable
    ? (_askOne ? (contextDo && _ctxIsBuilder ? `${_ctxName}: do it` : `Ask ${_ctxName}`)
      : askAgentIds.length ? `Ask ${askAgentIds.length} agents` : 'Send')
    : '';
  const placeholder = contextMode && editable
    ? (_askOne ? (contextDo ? `Tell ${_ctxName} what to do — it works directly, not on a planned task`
      : `Ask ${_ctxName} — it answers, nothing is changed`)
      : askAgentIds.length ? 'Ask them — each answers, nothing is changed'
        : 'Talk the project through with your team — nothing runs')
    : _basePlaceholder;

  // RUN NETWORK (PRODUCT.md §2): the whole network on this prompt — the legacy run, never the discussion
  const runNetworkReady = runNetwork && contextMode && editable && !modeLoading && _baseSendReady
    && !!prompt.trim();
  const fireSend = async ({ asRun = false } = {}) => {
    if (asRun ? !runNetworkReady : !sendReady) return;
    if (onTypingStop) onTypingStop();
    // Project rooms: refuse a normal run until the plan is finalized (the banner tells the user
    // to finalize in Tasks or Run planner). Belt-and-suspenders with the disabled Send button.
    if (projectRunBlocked) return;
    const text = prompt.trim();
    // A ROOM MENTION ("@Platform …", EXPERT-ROOMS.md §8): the message opens that Team's request form — nothing is sent
    // A CONSULT ("@AWS CCoE how do I…", EXPERT-ROOMS.md §13): the message goes to that expert as a question
    const expert = onExpertConsult ? taggedExperts.find((t) => text.includes(`@${t.label}`)) : null;
    if (expert) {
      const question = text.split(`@${expert.label}`).join(' ').replace(/\s+/g, ' ').trim();
      if (!question) return;
      setPrompt('');
      setTaggedExperts([]);
      try { await onExpertConsult({ expertNetworkId: expert.expertNetworkId, text: question }); } catch (e) { setPrompt(text); }
      return;
    }
    const team = onTeamRequest ? taggedTeams.find((t) => text.includes(`@${t.label}`)) : null;
    if (team) {
      setPrompt('');
      setTaggedTeams([]);
      onTeamRequest({ team: team.label, expertNetworkId: team.expertNetworkId, note: text });
      return;
    }
    if (contextMode && !asRun) {
      // CONTEXT: a room-discussion message (and, with an agent picked, its reply) — never a run.
      setPrompt('');
      try {
        // files shared here join the room's context at upload time (room-uploads); the message records them
        const files = (attachments.descriptors || []).map((d) => ({
          bucket: d.bucket, s3Key: d.s3Key, fileName: d.filename || d.fileName }));
        const ids = askAgentIds;
        const people = mentionSubsFor(text);
        await onSendContext(text, { agentId: ids[0] || '', agentIds: ids, attachments: files,
          ...(people.length ? { mentions: people } : {}),
          ...(ids.length ? { mode: contextDo && _ctxIsBuilder && ids.length === 1 ? 'do' : 'ask' } : {}) });
        attachments.reset();
        setTaggedPeople([]);
      } catch (err) {
        setPrompt(text);            // nothing lost; the room shows why it failed
      }
      return;
    }
    // SNAP BACK IMMEDIATELY (before awaiting the network round-trip): the user's Enter/Send IS the
    // event — the text has been captured, so clear the box + reset the two-deck expansion NOW
    // rather than leaving an empty tall window until the first AppSync reply lands. On failure we
    // restore the text so nothing is lost.
    const wasModify = composerState === COMPOSER_STATES.AWAITING_APPROVAL && modifyMode && iHoldClaim;
    if (composerState === COMPOSER_STATES.IDLE || wasModify) {
      setPrompt('');
    }
    try {
      if (composerState === COMPOSER_STATES.IDLE) {
        // PROJECT MODE routing: 'plan' → a PLAN-MODE run (planMode:true) that grooms the __planner__
        // ledger from the prompt + uploaded artifacts (writes nothing); 'task'/'wave' → an EXECUTION
        // run bound to one task or the ready wave. Non-project rooms send neither (legacy chat/exec).
        let _projectOpts = {};
        if (isProject && !asRun) {
          // Scope source, in priority order: (1) an explicit Tasks-tab groom selection; else (2) the
          // scope of a verbatim-accepted suggestion (e.g. "Plan X wave N" → confine to wave N) — so the
          // run matches its label. A hand-typed prompt (suggestionAccepted=false, no selection) → no
          // scope → the reducer defaults to the whole current milestone (unchanged behavior).
          const _effectiveGroom = (groomScope && groomScope.milestone)
            ? groomScope
            : (suggestionAccepted && suggestedGroomScope && suggestedGroomScope.milestone
                && prompt.trim() === String(suggestedNextPrompt || '').trim()
                ? suggestedGroomScope : null);
          _projectOpts = projectMode === 'plan'
            ? {
                planMode: true,
                // GROOM (§2.6): a surgical plan run confined to the selected/suggested scope.
                ...(_effectiveGroom && _effectiveGroom.milestone
                  ? { groomScope: {
                      milestone: _effectiveGroom.milestone,
                      // Partition: explicit task ids (row select) OR waves (§2.8) OR an epic (§0.1).
                      // Pass through whichever was supplied; runner expands waves/epic → task ids.
                      taskIds: _effectiveGroom.taskIds || [],
                      ...(Array.isArray(_effectiveGroom.waves) && _effectiveGroom.waves.length ? { waves: _effectiveGroom.waves } : {}),
                      ...(_effectiveGroom.epic ? { epic: _effectiveGroom.epic } : {}),
                      // EPIC EXPANSION (§0.1): author leaf children under this epic (only leaves run).
                      ...(_effectiveGroom.epicParent ? { epicParent: _effectiveGroom.epicParent } : {}),
                    } }
                  : {}),
                // PLAN TIER (§2.5): the user's per-milestone tier ('standard'|'verified'); 'auto' →
                // omit (runner uses architect hint + backstop).
                ...(planTier === 'standard' || planTier === 'verified' ? { planTier } : {}),
              }
            : projectMode === 'tasks'
            // RUN TASKS: the selection (dependency order) — the runner binds its ready layer, then
            // continues on its own step by step (stops on a failure, a task waiting on you, or Stop)
            ? { taskBinding: 'tasks', taskIds: [...((runSelection && runSelection.taskIds) || [])] }
            : { taskBinding: projectMode,
              // the task the prefill NAMES is the task that runs (Auto → the next ready one, shown in the text)
              // Verify too (M2): the proof lands in THAT task's folder, so the id must be known up front
              taskId: (projectMode === 'task' || projectMode === 'verify')
                ? (selectedTaskId || (autoTask && autoTask.id) || '') : '' };
        }
        const _people = mentionSubsFor(text);
        await onSendPrompt(text, {
          stagingId: attachments.stagingId,
          attachments: attachments.descriptors,
          correlationId: attachments.stagingId, // reuse staging id as correlation key
          ...(_people.length ? { mentions: _people } : {}),   // people tagged — notified, the run is unchanged
          ..._projectOpts,
        });
        setTaggedPeople([]);
      } else if (wasModify) {
        await onSendModify(text, {
          stagingId: attachments.stagingId,
          attachments: attachments.descriptors,
        });
        setModifyMode(false);
      }
      attachments.reset();
    } catch (err) {
      // Send failed — restore the draft so the user can retry without retyping.
      setPrompt(text);
      throw err;
    }
  };

  const handleAttachClick = () => {
    if (!attachEnabled) return;
    fileInputRef.current?.click();
  };

  const handleFiles = (e) => {
    const files = e.target.files;
    if (files && files.length) attachments.addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dismissComposerPromptTip = () => {
    try {
      sessionStorage.setItem(COMPOSER_PROMPT_TIP_KEY, '1');
    } catch {
      /* ignore private mode */
    }
    setComposerTipDismissed(true);
  };

  // Band priority while a run is active:
  //   1. builderCliWait → the interaction band (approval/question)
  //   2. activeBuilderRunId → "run in progress" band WITH the Stop action INSIDE
  //      the band (not a separate box) — always-present kill switch
  //   3. approvableMilestone → "plan complete — Approve {milestone}" band with the approve action
  //      INLINE (idea 2: the human gates the milestone right where the status shows, not buried in
  //      the Tasks tab). Only when idle (not mid-run) and the plan is actually ready.
  //   4. else → the dismissible heads-up tip
  const [suggestedUpdateBusy, setSuggestedUpdateBusy] = useState(false);
  const promptAlertBand = builderCliWait ? (
    <BuilderInteractionBand
      networkId={networkId}
      runId={builderCliWait.runId}
      poolId={poolId}
      waitEventId={builderCliWait.eventId}
      interaction={builderCliWait.interaction}
      promptSnippet={builderCliWait.prompt}
      onHandled={onBuilderCliHandled}
      readOnly={!canRespondApproval}
    />
  ) : (activeBuilderRunId && onStopRun) ? (
    <WorkbenchPromptAlertBand
      message={
        <span className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          Builder run in progress…
        </span>
      }
      primaryLabel={stoppingRun ? 'Stopping…' : 'Stop'}
      onPrimary={stoppingRun ? undefined : onStopRun}
      primaryTone="danger"
    />
  ) : (isProject && approvableMilestone && !roomIsRunning) ? (
    <WorkbenchPromptAlertBand
      message={
        <span className="flex items-center gap-1.5">
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          <span>
            <span className="font-medium text-gray-800">{approvableMilestone.name}</span>
            {' '}plan is complete — approve to move planning forward.
            {planAdvice}
          </span>
          {/* REVIEW jump — read/edit the plan before approving (escape hatch from "slide through"). */}
          <button
            type="button"
            data-testid="composer-review-milestone"
            onClick={() => onReviewMilestone(approvableMilestone.id)}
            className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-indigo-600 underline-offset-2 hover:bg-indigo-50 hover:underline"
          >
            Review
          </button>
        </span>
      }
      primaryLabel={approveMilestoneBusy ? 'Approving…' : `Approve ${approvableMilestone.name}`}
      onPrimary={approveMilestoneBusy ? undefined : () => onApproveMilestone(approvableMilestone.id)}
    />
  ) : (consultsWaiting && consultsWaiting.count > 0) ? (
    <div data-testid="composer-consults-waiting" className="w-full">
      <WorkbenchPromptAlertBand
        message={
          <span>
            <span className="font-medium text-gray-800">
              {consultsWaiting.count} question{consultsWaiting.count === 1 ? '' : 's'} waiting for your team
            </span>
            {consultsWaiting.first ? ` — "${String(consultsWaiting.first.question || '').slice(0, 90)}" from `
              + `${consultsWaiting.first.asker?.name || 'another room'}` : ''}
          </span>
        }
        primaryLabel="Answer"
        onPrimary={consultsWaiting.onReview}
      />
    </div>
  ) : (suggestedUpdate && onSuggestedUpdate && !roomIsRunning) ? (
    <div data-testid="composer-suggested-update" className="w-full">
      <WorkbenchPromptAlertBand
        message={
          <span>
            <span className="font-medium text-gray-800">The network&apos;s suggested plan was updated</span>
            {' '}(v{suggestedUpdate.fromVersion} → v{suggestedUpdate.toVersion}
            {Array.isArray(suggestedUpdate.milestones) && suggestedUpdate.milestones.length
              ? `: ${suggestedUpdate.milestones.join(', ')}` : ''}). This room still has it unchanged — update to it?
          </span>
        }
        primaryLabel={suggestedUpdateBusy ? 'Updating…' : 'Update'}
        onPrimary={suggestedUpdateBusy ? undefined : async () => {
          setSuggestedUpdateBusy(true);
          try { await onSuggestedUpdate('update'); } finally { setSuggestedUpdateBusy(false); }
        }}
        onDismiss={() => onSuggestedUpdate('dismiss')}
      />
    </div>
  ) : (composerTipDismissed || !roomPremise || roomIsRunning) ? null : (
    <WorkbenchPromptAlertBand
      message={
        <>
          <span className="font-medium text-gray-800">Working premise:</span>{' '}
          {roomPremise}
        </>
      }
      progressPct={
        typeof roomGuidance?.completion?.pct === 'number'
          ? roomGuidance.completion.pct
          : null
      }
      primaryLabel="Got it"
      onPrimary={dismissComposerPromptTip}
      onDismiss={dismissComposerPromptTip}
    />
  );

  return (
    // pb-2 (not pb-6): the composer is no longer the room's true bottom — the agent-stream dock sits
    // directly below it now. A big bottom pad here read as dead space between the mode bar and the
    // dock. Keep it tight so the prompt sits close to the dock.
    <div className="shrink-0 px-6 pb-2">
      <div className="w-full">{/* full width, aligned with the wide stream (the only layout — 2026-10-10) */}
        {composerState === COMPOSER_STATES.AWAITING_APPROVAL && pendingApproval ? (
          <ApprovalDeck
            pendingApproval={pendingApproval}
            iHoldClaim={iHoldClaim}
            peerHoldsClaim={peerHoldsClaim}
            peerLabel={peerLabel}
            modifyMode={modifyMode}
            approvalBusy={approvalBusy}
            frozen={frozen}
            frozenReason={frozenReason}
            onClaim={onClaim}
            onApprove={onApprove}
            onDeny={onDeny}
            advice={gateAdvice}
            onStartModify={() => setModifyMode(true)}
            onCancelModify={() => {
              setModifyMode(false);
              setPrompt('');
              attachments.reset();
            }}
            onDelegate={onDelegate}
            onEscalate={onEscalate}
          />
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void fireSend();
          }}
          className="relative z-20"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ROOM_UPLOAD_ACCEPT}
            className="hidden"
            onChange={handleFiles}
          />
          {/* COMMAND DECK (owner 2026-10-03) — ONE Slack-style box, three decks: upper = attach (+) | the
              mode switcher + its controls + ✦; middle = the prompt; lower = agent streams + scope chips | Send.
              The mode controls hide while the room RUNS (stale mid-run) and return when it goes IDLE. */}
          <CommandDeck
            testId="room-command-deck"
            alertBand={promptAlertBand}
            topLeft={(<>
              {/* THE SHARED UPPER-LEFT GROUP (DeckLeftGroup — the same in every prompt box) */}
              <DeckLeftGroup testId="room-composer" onFormat={formatPrompt} formatDisabled={!editable}
                onAttach={handleAttachClick} attachDisabled={!attachEnabled}
                onSnippet={() => setSnippetOpen(true)} snippetDisabled={!attachEnabled} />
              {deckTools}
              {snippetOpen ? (
                <CodeSnippetDialog onCancel={() => setSnippetOpen(false)}
                  onAdd={(file) => { setSnippetOpen(false); attachments.addFiles([file]); }} />
              ) : null}
              {deckStatus}
            </>)}
            topRight={roomIsRunning ? null : (
              <div className="flex flex-wrap items-center justify-end gap-1.5 text-[11px] leading-snug text-gray-400">
            {modeLoading && isProject ? (
              <span data-testid="composer-mode-loading" aria-label="Loading the room's mode"
                className="inline-block h-5 w-44 animate-pulse rounded-md bg-gray-200/70" />
            ) : isProject && editable ? (
              <span data-testid="composer-project-mode" className="inline-flex items-center gap-1">
                <span className="inline-flex overflow-hidden rounded-md ring-1 ring-gray-200">
                  {contextOnly && typeof onOpenPlanGate === 'function' && !keptConversational ? (
                    <button type="button" data-testid="composer-plan-gate" onClick={() => onOpenPlanGate()}
                      disabled={!canSwitchMode}
                      title={canSwitchMode ? 'Plan this room — draft milestones from its context, edit, approve'
                        : _LOCK_TITLE}
                      className="order-last border-l border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:text-gray-400">
                      Plan…
                    </button>
                  ) : null}
                  {typeof onSendContext === 'function' ? (
                    <button
                      type="button"
                      data-testid="composer-mode-context"
                      onClick={() => pickMode('context')}
                      disabled={_locked('context')}
                      title={_locked('context') ? _LOCK_TITLE : 'Context — talk the project through with your team, or ask one agent; nothing runs'}
                      className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${projectMode === 'context' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Context
                    </button>
                  ) : null}
                  {contextOnly ? null : (<>
                  <button
                    type="button"
                    data-testid="composer-mode-plan"
                    disabled={_locked('plan')}
                    onClick={async () => {
                      // M5: Context → Plan offers to build the context first (the room asks)
                      if (projectMode === 'context' && typeof onLeaveContext === 'function') {
                        const go = await onLeaveContext('plan');
                        if (!go) return;
                      }
                      pickMode('plan');
                    }}
                    title={_locked("plan") ? _LOCK_TITLE : "Plan mode — describe the changes + attach artifacts; drafts the plan for review (writes nothing)"}
                    className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${projectMode === 'plan' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Plan
                  </button>
                  {verifyOnly ? (
                    <span data-testid="composer-kind-verify-only" className="sr-only">
                      Plan and verify project — people do the work
                    </span>
                  ) : (<>
                  {/* RUN ▾ — one split button: the main part selects the last-used Run style, the arrow
                      switches it (PortalMenu). Disabled until a milestone is locked + something is ready. */}
                  <button
                    type="button"
                    data-testid="composer-mode-run"
                    onClick={() => canRunTasks && pickMode(runStyle)}
                    disabled={!canRunTasks || _locked('run')}
                    title={canRunTasks
                      ? (runStyle === 'wave' ? `Run every ready task (${readyList.length}) in parallel`
                        : runStyle === 'tasks' ? `Run the ${_runCount} selected tasks in order — ${_runSteps} step${_runSteps === 1 ? '' : 's'}`
                        : 'Run one task — the next ready one, or pick it')
                      : 'Lock a milestone (Tasks tab) to run its tasks'}
                    className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${projectMode === 'task' || projectMode === 'wave' || projectMode === 'tasks' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:cursor-not-allowed disabled:text-gray-300`}
                  >
                    {runStyle === 'wave' ? 'Run wave' : runStyle === 'tasks' ? `Run tasks (${_runCount})` : 'Run task'}
                  </button>
                  <button
                    type="button"
                    ref={runMenuRef}
                    data-testid="composer-run-menu"
                    aria-label="Choose how to run"
                    aria-haspopup="menu"
                    aria-expanded={runMenuOpen}
                    onClick={() => canRunTasks && setRunMenuOpen((v) => !v)}
                    disabled={!canRunTasks}
                    className={`border-l border-gray-200 px-1 py-0.5 transition-colors ${projectMode === 'task' || projectMode === 'wave' || projectMode === 'tasks' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:cursor-not-allowed disabled:text-gray-300`}
                  >
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  </button>
                  <PortalMenu anchorRef={runMenuRef} open={runMenuOpen} onClose={() => setRunMenuOpen(false)} align="top-left">
                    <div className="w-60 py-1 text-[12px]" role="none">
                      {[['task', 'Run task', 'One task — the next ready one, or pick it'],
                        ['wave', 'Run wave', `Every ready task (${readyList.length}) in parallel`],
                        ['tasks', _runCount ? `Run tasks (${_runCount})` : 'Run tasks',
                          _runCount ? `The selected tasks in order — ${_runSteps} step${_runSteps === 1 ? '' : 's'}, each starting when the one before is done`
                            : 'Select tasks in a locked milestone (Tasks tab) to run them in order']].map(([k, label, hint]) => (
                        <button key={k} type="button" role="menuitem" data-testid={`composer-run-option-${k}`}
                          disabled={k === 'tasks' && !_runCount}
                          onClick={() => { setRunStyle(k); pickMode(k); setRunMenuOpen(false); }}
                          className={`flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${runStyle === k ? 'text-indigo-700' : 'text-gray-700'}`}>
                          <span className="font-medium">{label}</span>
                          <span className="text-[11px] text-gray-400">{hint}</span>
                        </button>
                      ))}
                    </div>
                  </PortalMenu>
                  </>)}
                  <button
                    type="button"
                    data-testid="composer-mode-verify"
                    onClick={() => canVerify && pickMode('verify')}
                    disabled={!canVerify || _locked('verify')}
                    title={canVerify
                      ? 'Verify — attach the proof that a task is done; agents check it against the definition of done (you then confirm it)'
                      : 'Lock a milestone (Tasks tab) to verify its tasks'}
                    className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${projectMode === 'verify' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:cursor-not-allowed disabled:text-gray-300`}
                  >
                    Verify
                  </button>
                  </>)}
                </span>
                {/* Mode-specific trailing hint: Plan = a tiny SCOPE hint (the selection prefilled the
                    prompt above; this just shows/clears the underlying scope — NOT a chip) or the
                    default nudge; Task = picker; Wave = count. */}
                {projectMode === 'context' ? (
                  <select
                    data-testid="composer-context-agent"
                    value={contextAgent}
                    onChange={(e) => setContextAgent(e.target.value)}
                    className="max-w-[14rem] truncate rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-700"
                    title="Who answers — No agent keeps it between people; pick an agent to ask it directly"
                  >
                    <option value="">No agent</option>
                    {(discussionAgents || []).map((a) => (
                      <option key={a.id} value={a.id}>{a.name || a.id}</option>
                    ))}
                  </select>
                ) : null}
                {/* THE ROOM'S MODEL (MODES.md §3e) — right of the agent: the tier agents answer on in this room */}
                {projectMode === 'context' ? (
                  <ContextModelPicker value={contextModel} canSet={canSetContextModel} setByName={contextModelSetByName}
                    onChange={onSetContextModel} />
                ) : null}
                {projectMode === 'context' ? (askAgentIds.length ? (<>
                  <span data-testid="composer-context-askdo" className="inline-flex overflow-hidden rounded-md ring-1 ring-gray-200">
                    {[[false, 'Ask', 'It answers — nothing is changed'],
                      [true, 'Do', _ctxIsBuilder ? 'It does the work, in its own area of the project files'
                        : keptConversational ? 'This room is kept conversational — agents answer here, nothing is built'
                        : !_askOne ? 'Do works with one agent at a time — name just one to have it do the work'
                          : `${_ctxName} can only answer — pick a builder agent to do work`]].map(([v, label, hint]) => (
                      <button key={label} type="button" data-testid={`composer-context-${label.toLowerCase()}`}
                        onClick={() => setContextDo(v)} title={hint} aria-pressed={contextDo === v}
                        disabled={v && !_ctxIsBuilder}
                        className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${contextDo === v ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:cursor-not-allowed disabled:text-gray-300`}>
                        {label}
                      </button>
                    ))}
                  </span>
                  <span data-testid="composer-web" data-on={internetSearch ? 'true' : 'false'}
                    title={internetSearch ? 'Internet search is on for this room — the agent may search the web; its answer shows the sources'
                      : 'Internet search is off for this room — the agent answers from the room\u2019s context only'}
                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] ${internetSearch ? 'text-indigo-700' : 'text-gray-400 line-through decoration-gray-300'}`}>
                    <Globe className="h-3 w-3" aria-hidden />Web
                  </span>
                </>) : null) : (projectMode === 'plan' || projectMode === 'tasks') ? null : (projectMode === 'task' || projectMode === 'verify') ? (
                  <select
                    data-testid="composer-task-select"
                    value={selectedTaskId}
                    onChange={(e) => setSelectedTaskId(e.target.value)}
                    className="max-w-[16rem] truncate rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-700"
                    title={projectMode === 'verify' ? 'Which task the attached proof is for' : 'Which task this run executes'}
                  >
                    <option value="">
                      {autoTask ? `Auto — next: ${autoTask.id} ${autoTask.title || ''}`.trim() : 'Auto — next ready'}
                    </option>
                    {readyList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.id}{t.title ? ` · ${t.title}` : ''}{t.leadAgent ? ` (${t.leadAgent})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700 ring-1 ring-emerald-200">
                    {readyList.length} ready{currentMilestone ? ` · ${currentMilestone}` : ''}
                  </span>
                )}
              </span>
            ) : null}
            <ComposerHint
              inline
              bullets={COMPOSER_TIP_BULLETS}
              guidance={roomGuidance}
              // GUIDED-JOURNEY pipeline (Plan→Review→Finalize→Run) — rendered at the TOP of the ✦
              // popover (not an always-on band under the composer), so "where am I" is one click away.
              pipeline={isProject && projectStage
                ? <ProjectPipeline stage={projectStage} nextLabel={suggestedNextPrompt} />
                : null}
              guidanceAsOf={roomGuidanceAt}
              // No onUsePrompt: the popover is CONTEXT (premise + completion), not a next-step picker.
              // The deterministic proposal (placeholder/Tab + Approve band) owns "what's next".
            />
              </div>
            )}
            bottomLeft={(
              <>
                {typeof onToggleDock === 'function' ? (
                  <button
                    type="button"
                    data-testid="dock-show"
                    onClick={onToggleDock}
                    aria-pressed={!!dockOpen}
                    title={dockOpen ? 'Hide agent streams' : 'Show agent streams (dock)'}
                    className={`${DECK_ICON_BUTTON} ${dockOpen ? 'bg-gray-200/70 text-gray-900' : ''}`}
                  >
                    <PanelBottom className="h-3.5 w-3.5" aria-hidden />
                    <span>Agent streams</span>
                  </button>
                ) : null}
                {composerState === COMPOSER_STATES.AWAITING_APPROVAL && modifyMode && iHoldClaim ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                    Modify mode — Send replies to the agent
                  </span>
                ) : null}
                {!roomIsRunning && isProject && editable && projectMode === 'plan' && groomScope && groomScope.milestone ? (
                    <span
                      data-testid="composer-groom-scope-hint"
                      className="inline-flex items-center gap-1 text-[11px] text-fuchsia-600"
                      title={groomScope.kind === 'wave'
                        ? 'This Send is scoped to the selected wave(s) — edit the prompt above, then Send'
                        : 'This Send is scoped to the selected tasks — edit the prompt above, then Send'}
                    >
                      {/* Scope surfaced as a subtle inline line (name, not id; waves 1-indexed). The
                          ACTION now lives in the prefilled prompt text — this just makes the silent
                          scope visible + clearable. */}
                      scoped to {groomScope.milestoneName || groomScope.milestone}
                      {(groomScope.waves || []).length
                        ? ` wave${groomScope.waves.length === 1 ? '' : 's'} ${_waveLabels(groomScope.waves)}`
                        : (groomScope.taskIds || []).length ? ` · ${(groomScope.taskIds).length} task${groomScope.taskIds.length === 1 ? '' : 's'}` : ''}
                      {typeof groomScope.onClear === 'function' ? (
                        <button
                          type="button"
                          onClick={groomScope.onClear}
                          className="ml-0.5 rounded px-1 text-fuchsia-500 hover:bg-fuchsia-100 hover:text-fuchsia-700"
                          aria-label="Clear scope"
                          title="Clear — plan the whole milestone instead"
                        >
                          ✕ clear
                        </button>
                      ) : null}
                    </span>
                ) : null}
                {contextMode && mentioned.length ? (
                  <span data-testid="composer-mentioned"
                    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] ring-1 ${doTooMany ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-indigo-50 text-indigo-700 ring-indigo-200'}`}
                    title={doTooMany ? 'Do works with one agent at a time — switch to Ask to hear from several' : 'Each named agent answers'}>
                    {doTooMany ? 'Do: one agent at a time' : `asking ${mentioned.map((id) => {
                      const a = (discussionAgents || []).find((x) => x.id === id); return `@${(a && a.name) || id}`; }).join(' ')}`}
                  </span>
                ) : null}
                {!roomIsRunning && isProject && editable && projectMode === 'tasks' ? (
                  <span data-testid="composer-run-tasks-hint"
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700 ring-1 ring-indigo-200"
                    title="Each step starts when the one before is done; it stops if a run fails, a task is waiting on you, or you press Stop">
                    starts {_runCount} task{_runCount === 1 ? '' : 's'} in {_runSteps} run{_runSteps === 1 ? '' : 's'}
                    {runSelection && typeof runSelection.onClear === 'function' ? (
                      <button type="button" onClick={runSelection.onClear} aria-label="Clear selection"
                        className="ml-0.5 rounded px-1 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700">✕ clear</button>
                    ) : null}
                  </span>
                ) : null}
            {/* CONNECTOR READINESS (Unit D) — SOFT WARN, not a block: the task(s) about to run need
                connectors that aren't tested yet. Advisory chip; Send still works. */}
            {connectorWarning ? (
              <span
                data-testid="composer-connector-warning"
                className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 ring-1 ring-amber-200"
                title={`Not tested: ${connectorWarning.join(', ')} — configure in the Tasks tab. The run will proceed.`}
              >
                <ClipboardList className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {connectorWarning.length} connection{connectorWarning.length === 1 ? '' : 's'} not tested
              </span>
            ) : null}
            {verbose && roomGuidance?.completion?.basis ? (
              <span>{roomGuidance.completion.basis}</span>
            ) : null}
              </>
            )}
            bottomRight={(
              <span className="inline-flex items-center gap-1.5">
                {runNetwork && contextMode && editable ? (
                  <button type="button" data-testid="composer-run-network" disabled={!runNetworkReady || sending}
                    onClick={() => { fireSend({ asRun: true }).catch(() => {}); }}
                    title="Run the whole network on this prompt (not just the agents you ask)"
                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:ring-gray-200">
                    ▶ Run network
                  </button>
                ) : null}
                <SendButton sendReady={sendReady && !projectRunBlocked} sending={sending} label={sendLabel} />
              </span>
            )}
          >

              <div className="flex flex-col gap-2">
                {attachments.items.length > 0 ? (
                  <AttachmentChips
                    items={attachments.items}
                    onRemove={attachments.removeItem}
                    disabled={!editable}
                  />
                ) : null}
                {typingLine ? (
                  <div className="px-1 pb-0.5 text-[11px] italic text-gray-500" data-testid="room-typing" aria-live="polite">{typingLine}</div>
                ) : null}
                <div className="relative">
                {mentionTags ? (
                  <div ref={mirrorRef} aria-hidden data-testid="composer-mention-mirror"
                    className={getWorkbenchPromptInputClassName({ disabled: !editable, size: 'deck' })
                      .replace('relative z-10', 'pointer-events-none absolute inset-0 z-0').replace('overflow-y-auto', 'overflow-hidden')}>
                    {(() => { let at = 0; return mentionTags.map((x, i) => {
                      const start = at; at += x.text.length;
                      if (!x.tag) return <span key={i}>{x.text}</span>;
                      const on = chipSel === start;   // the clicked chip — one Backspace removes it
                      return (
                        <span key={i} data-testid="composer-mention-tag" data-kind={x.kind} data-selected={on ? 'true' : undefined}
                          className={`rounded ${x.kind === 'person'
                            ? (on ? 'bg-amber-300 text-amber-950 shadow-[0_0_0_2px_theme(colors.amber.300)]' : 'bg-amber-100 text-amber-900 shadow-[0_0_0_2px_theme(colors.amber.100)]')
                            : (on ? 'bg-indigo-600 text-white shadow-[0_0_0_2px_theme(colors.indigo.600)]' : 'bg-indigo-100 text-indigo-700 shadow-[0_0_0_2px_theme(colors.indigo.100)]')}`}>{x.text}</span>
                      );
                    }); })()}
                    {/* a trailing newline needs a glyph to take its line, as the field does */}
                    {prompt.endsWith('\n') ? '\u200b' : null}
                  </div>
                ) : null}
                <textarea
                  ref={promptFieldRef}
                  onScroll={(e) => { if (mirrorRef.current) mirrorRef.current.scrollTop = e.currentTarget.scrollTop; }}
                  onSelect={(e) => syncChipSel(e.currentTarget)}
                  // a click INSIDE a tag selects the whole tag (one Backspace then removes it)
                  onMouseUp={(e) => {
                    const el = e.currentTarget;
                    const r = chipSelection(el.selectionStart, el.selectionEnd, chipRanges, { click: true });
                    if (r) { el.setSelectionRange(r[0], r[1]); syncChipSel(el); }
                  }}
                  // the keyboard caret never rests inside a tag — it hops over it
                  onKeyUp={(e) => {
                    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key) || e.shiftKey) return;
                    const el = e.currentTarget;
                    const r = chipSelection(el.selectionStart, el.selectionEnd, chipRanges,
                      { dir: e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0 });
                    if (r) el.setSelectionRange(r[0], r[1]);
                  }}
                  onBlur={() => { if (onTypingStop) onTypingStop(); }}
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value); setSuggestionAccepted(false);
                    if (e.target.value.trim()) { if (onTyping) onTyping(); } else if (onTypingStop) onTypingStop();
                    setMentionQ(canMention ? mentionAt(e.target.value, e.target.selectionStart) : null);
                    setMentionIdx(0);
                  }}
                  onKeyDown={(e) => {
                    // the @mention picker owns ↑ ↓ Enter Tab Esc while it's open
                    if (mentionQ && mentionMatches.length) {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        setMentionIdx((i) => (i + (e.key === 'ArrowDown' ? 1 : mentionMatches.length - 1)) % mentionMatches.length);
                        return;
                      }
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        pickMention(mentionMatches[mentionIdx] || mentionMatches[0]);
                        return;
                      }
                      if (e.key === 'Escape') { e.preventDefault(); setMentionQ(null); return; }
                    }
                    // CHIPS: Backspace / Delete on a tag removes the WHOLE tag (never letter by letter)
                    if ((e.key === 'Backspace' || e.key === 'Delete') && chipRanges.length && !e.metaKey && !e.altKey && !e.ctrlKey) {
                      const el = e.currentTarget;
                      const cut = chipDelete(prompt, el.selectionStart, el.selectionEnd, e.key, chipRanges);
                      if (cut) {
                        e.preventDefault();
                        setPromptSel({ value: cut.text, start: cut.caret, end: cut.caret });
                        setChipSel(null);
                        setMentionQ(null);
                        return;
                      }
                    }
                    // ⌘B / ⌘I / ⌘⇧7 / ⌘⇧8 (Slack's) — MODES.md §3c
                    const fmt = formatShortcut(e);
                    if (fmt) { e.preventDefault(); formatPrompt(fmt); return; }
                    // Shift+Enter in a list item → the next item; on an empty item → end the list
                    if (e.key === 'Enter' && e.shiftKey && e.currentTarget.selectionStart === e.currentTarget.selectionEnd) {
                      const next = continueList(prompt, e.currentTarget.selectionStart);
                      if (next) { e.preventDefault(); setPromptSel(next); return; }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void fireSend();
                    }
                    // TAB-TO-FILL (idea 1): on an EMPTY editable field with a deterministic
                    // suggestion showing as the placeholder, Tab accepts it as real text (then the
                    // user edits or Sends). Only when empty so Tab still tabs-out once you're typing.
                    if (e.key === 'Tab' && !e.shiftKey && showSuggestGhost) {
                      e.preventDefault();
                      setPrompt(suggestedNextPrompt);
                      setSuggestionAccepted(true);   // Send may apply the suggestion's groomScope
                      setTimeout(() => promptFieldRef.current?.focus(), 0);
                    }
                  }}
                  disabled={!editable}
                  rows={1}
                  // When empty + editable + idle, the deterministic proposal IS the placeholder
                  // (Tab accepts it — see the "Tab ⇥" hint below). Otherwise the normal placeholder.
                  placeholder={showSuggestGhost ? suggestedNextPrompt : placeholder}
                  aria-multiline="true"
                  className={`${getWorkbenchPromptInputClassName({ disabled: !editable, size: 'deck' })}${mentionTags ? ' bg-transparent' : ''}${chipSel != null ? ' selection:bg-transparent' : ''}`}
                  style={mentionTags ? { ...WORKBENCH_PROMPT_FIELD_STYLE, color: 'transparent', caretColor: '#111827' } : WORKBENCH_PROMPT_FIELD_STYLE}
                />
                </div>
                <PortalMenu anchorRef={promptFieldRef} open={!!(mentionQ && mentionMatches.length)}
                  onClose={() => setMentionQ(null)} align="top-left">
                  <div className="max-h-72 w-56 overflow-y-auto py-1 text-[12px]" role="listbox" data-testid="composer-mention-picker">
                    {mentionMatches.map((m, i) => (
                      <button key={m.key} type="button" role="option" aria-selected={i === mentionIdx}
                        ref={i === mentionIdx ? (el) => { if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' }); } : undefined}
                        data-testid={m.kind === 'agent' ? `composer-mention-${m.id}` : m.kind === 'team'
                          ? `composer-mention-team-${m.label}` : m.kind === 'expert'
                            ? `composer-mention-expert-${m.expertNetworkId}` : `composer-mention-person-${m.sub}`}
                        title={m.kind === 'person' ? `${m.label}${m.email && m.email !== m.label ? ` · ${m.email}` : ''} — they'll be notified` : undefined}
                        onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}
                        className={`flex w-full items-center gap-2 px-3 py-1 text-left ${i === mentionIdx ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                        <span className={`inline-flex h-5 w-5 items-center justify-center text-[10px] font-semibold ${m.kind === 'person'
                          ? 'rounded-full bg-slate-700 text-white' : 'rounded bg-indigo-100 text-indigo-700'}`}>
                          {String(m.label).charAt(0).toUpperCase()}</span>
                        <span className="truncate">{m.label}</span>
                        <span className="ml-auto text-[10px] text-gray-400">{m.kind === 'person' ? 'person' : m.kind === 'team' ? 'expert team' : m.kind === 'expert' ? 'expert · ask' : 'agent'}</span>
                      </button>
                    ))}
                  </div>
                </PortalMenu>
                {showSuggestGhost ? (
                  <div className="-mt-1 flex items-center gap-2 pl-1 text-[10px] text-gray-400">
                    <span className="pointer-events-none">
                      Press <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-sans text-[10px] text-gray-500">Tab ⇥</kbd> to use this suggestion
                    </span>
                    {/* REVIEW — jump to the milestone in the Tasks tab to read/edit before acting. */}
                    {reviewMilestone ? (
                      <button
                        type="button"
                        data-testid="composer-review-inline"
                        onClick={() => onReviewMilestone(reviewMilestone.id)}
                        className="rounded px-1 text-[10px] font-medium text-indigo-500 hover:bg-indigo-50 hover:underline"
                      >
                        Review {reviewMilestone.name}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
          </CommandDeck>
        </form>
      </div>
    </div>
  );
}

function SendButton({ sendReady, sending, label = '' }) {
  if (label) {
    // CONTEXT MODE: the button says what happens (Send · Ask Architect · Builder: do it)
    return (
      <button type="submit" disabled={!sendReady} data-testid="composer-send-labelled" aria-label={label}
        className={`inline-flex h-7 max-w-[14rem] shrink-0 items-center truncate px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
          !sendReady ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
        {sending ? 'Sending…' : label}
      </button>
    );
  }
  return (
    <button
      type="submit"
      disabled={!sendReady}
      className={`
        inline-flex h-7 w-8 shrink-0 items-center justify-center rounded font-medium transition-colors
        ${
          !sendReady
            ? 'bg-transparent text-gray-300 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }
      `}
      aria-label="Send"
    >
      {sending ? (
        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        // Slack-style solid send arrow
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path d="M1.5 2.1a.75.75 0 0 1 .82-.14l16 7.35a.75.75 0 0 1 0 1.37l-16 7.35a.75.75 0 0 1-1.05-.86L3.1 10 1.27 2.93a.75.75 0 0 1 .23-.83ZM4.43 10.75l-1.25 4.86L15.6 10 3.18 4.39l1.25 4.86h5.82a.75.75 0 0 1 0 1.5H4.43Z" />
        </svg>
      )}
    </button>
  );
}

/** ENGINE §8.16 — an agent step that needs a person's approval: show WHAT it is about to do and what it is based on
 *  (the step's ``design`` from APPROVAL_REQUESTED). Other approval kinds render nothing here. */
export function AgentStepPreview({ design }) {
  if (!design || design.kind !== 'agent-step' || !design.step) return null;
  return (
    <div className="mt-2 text-xs text-amber-900" data-testid="approval-agent-step">
      <p className="whitespace-pre-wrap line-clamp-6">{design.step}</p>
      {design.access ? <p className="mt-1 font-medium text-amber-800" data-testid="approval-agent-access">{design.access}</p> : null}
      {design.basedOn ? (
        <details className="mt-1">
          <summary className="cursor-pointer text-amber-800/80">What it&apos;s based on</summary>
          <p className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap text-amber-900/90">{design.basedOn}</p>
        </details>
      ) : null}
    </div>
  );
}

function ApprovalDeck({
  pendingApproval,
  iHoldClaim,
  peerHoldsClaim,
  peerLabel,
  modifyMode,
  approvalBusy,
  frozen = false,
  frozenReason = '',
  onClaim,
  onApprove,
  onDeny,
  onStartModify,
  onCancelModify,
  onDelegate,
  onEscalate,
  advice = null,
}) {
  const agentName = pendingApproval?.agentName || 'an agent';
  // When the room is frozen (PAUSED / RETIRED / ARCHIVED) every approval
  // action is disabled — backend rejects the wire calls anyway, but we
  // grey them out up front so the UI tells the truth.
  const lockTitle = frozen
    ? frozenReason || 'Frozen by lifecycle state — approvals are disabled.'
    : '';
  return (
    <div className="mb-2 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-amber-900">
          <span className="font-semibold">Approval required</span>
          {' · '}
          <span className="text-amber-800">{agentName}</span>
        </p>
        <div className="flex items-center gap-2 text-[11px] text-amber-900/80">
          {frozen ? (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 font-medium text-gray-700" title={lockTitle}>
              Frozen
            </span>
          ) : iHoldClaim ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-900">
              You&apos;re reviewing
            </span>
          ) : peerHoldsClaim ? (
            <span className="rounded-full bg-amber-200/80 px-2 py-0.5 font-medium text-amber-900">
              {peerLabel || 'Someone'} is reviewing
            </span>
          ) : null}
        </div>
      </div>
      {advice}
      <AgentStepPreview design={pendingApproval?.design} />

      <div className="mt-2 flex flex-wrap gap-2">
        {!iHoldClaim ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={frozen || approvalBusy || peerHoldsClaim}
            className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
            title={
              frozen
                ? lockTitle
                : peerHoldsClaim
                  ? 'Another supervisor is reviewing'
                  : 'Take ownership of this approval'
            }
          >
            {peerHoldsClaim ? 'Claimed by peer' : 'Claim to review'}
          </button>
        ) : null}

        {!modifyMode ? (
          <>
            <button
              type="button"
              onClick={onApprove}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : undefined}
            >
              <Check className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Approve
            </button>
            <button
              type="button"
              onClick={onDeny}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : undefined}
            >
              <XIcon className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Deny
            </button>
            <button
              type="button"
              onClick={onStartModify}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : 'Edit / extend the agent draft and resume the run with your changes'}
            >
              <Pencil className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Modify
            </button>
            {iHoldClaim ? (
              <button
                type="button"
                onClick={onDelegate}
                disabled={frozen || approvalBusy}
                className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
                title={frozen ? lockTitle : undefined}
              >
                Delegate
              </button>
            ) : null}
            <button
              type="button"
              onClick={onEscalate}
              disabled={frozen || approvalBusy}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : 'Escalate to workspace admins'}
            >
              Escalate
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onCancelModify}
            disabled={approvalBusy}
            className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
          >
            Cancel modification
          </button>
        )}
      </div>
    </div>
  );
}
